/**
 * Formatting utilities for display values
 */

import type { EncoderStreamInfo } from '@shared/types';

/**
 * Format duration in seconds to human-readable string
 * @example formatDuration(3661) => "1h 1m 1s"
 * @example formatDuration(125) => "2m 5s"
 * @example formatDuration(45) => "45s"
 * @example formatDuration(null) => "Live"
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return 'Live';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
}

/**
 * Format file size in bytes to human-readable string
 * @example formatFileSize(1073741824) => "1.0 GB"
 * @example formatFileSize(1048576) => "1.0 MB"
 * @example formatFileSize(1024) => "1.0 KB"
 * @example formatFileSize(500) => "500 B"
 * @example formatFileSize(null) => "-"
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '-';

  if (bytes >= 1_000_000_000) {
    return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
  }
  if (bytes >= 1_000_000) {
    return `${(bytes / 1_000_000).toFixed(1)} MB`;
  }
  if (bytes >= 1_000) {
    return `${(bytes / 1_000).toFixed(1)} KB`;
  }
  return `${bytes} B`;
}

/**
 * Format bitrate in bits/sec to human-readable string
 * @example formatBitrate(1200000) => "1.2 Mbps"
 * @example formatBitrate(500000) => "500 kbps"
 */
export function formatBitrate(bits: number): string {
  if (bits >= 1_000_000) {
    const mbps = bits / 1_000_000;
    return `${mbps.toFixed(1)} Mbps`;
  }
  const kbps = Math.round(bits / 1000);
  return `${kbps} kbps`;
}

/**
 * Format resolution from width and height
 * @example formatResolution(1920, 1080) => "1920x1080"
 */
export function formatResolution(width: number, height: number): string {
  return `${width}x${height}`;
}

/**
 * Parse and format frame rate from fraction string
 * @example formatFrameRate("30000/1001") => "29.97 fps"
 * @example formatFrameRate("30/1") => "30 fps"
 * @example formatFrameRate("25/1") => "25 fps"
 */
export function formatFrameRate(fraction: string): string {
  if (!fraction || !fraction.includes('/')) {
    return fraction || '-';
  }

  const [numerator, denominator] = fraction.split('/').map(Number);
  if (!denominator || denominator === 0) {
    return fraction;
  }

  const fps = numerator / denominator;

  // Common frame rates - show cleaner values
  if (Math.abs(fps - 29.97) < 0.01) return '29.97 fps';
  if (Math.abs(fps - 59.94) < 0.01) return '59.94 fps';
  if (Math.abs(fps - 23.976) < 0.01) return '23.98 fps';

  // For whole numbers, show without decimals
  if (Number.isInteger(fps)) {
    return `${fps} fps`;
  }

  return `${fps.toFixed(2)} fps`;
}

/**
 * Format encoder stream info field or return dash
 * @example formatStreamField(streamInfo, 'resolution') => "1920x1080"
 * @example formatStreamField(undefined, 'bitrate') => "-"
 */
export function formatStreamField(
  streamInfo: EncoderStreamInfo | undefined,
  field: 'resolution' | 'bitrate' | 'frameRate' | 'aspectRatio' | 'hasCaptions'
): string {
  if (!streamInfo) return '-';

  switch (field) {
    case 'resolution':
      return streamInfo.width && streamInfo.height
        ? formatResolution(streamInfo.width, streamInfo.height)
        : '-';
    case 'bitrate':
      return streamInfo.bitrate ? formatBitrate(streamInfo.bitrate) : '-';
    case 'frameRate':
      return streamInfo.frameRate ? formatFrameRate(streamInfo.frameRate) : '-';
    case 'aspectRatio':
      return streamInfo.aspectRatio || '-';
    case 'hasCaptions':
      return streamInfo.hasCaptions === undefined ? '-' : streamInfo.hasCaptions ? 'Yes' : 'No';
  }
}

/**
 * Format a date string to relative time
 * @example formatRelativeTime("2025-01-01T12:00:00Z") => "2h ago"
 * @example formatRelativeTime(null) => ""
 */
export function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

/**
 * Parse frame rate fraction string to number
 * @example parseFrameRateFraction("30000/1001") => 29.97
 * @example parseFrameRateFraction("30/1") => 30
 * @example parseFrameRateFraction(undefined) => null
 */
export function parseFrameRateFraction(fraction: string | undefined): number | null {
  if (!fraction || !fraction.includes('/')) return null;
  const [num, den] = fraction.split('/').map(Number);
  if (!den || den === 0) return null;
  return num / den;
}

/**
 * Format bitrate from bytes/sec to human-readable string
 * (streamInfo stores bitrate in bytes/sec, not bits/sec)
 * @example formatBitrateFromBytes(500000) => "4.0 Mbps"
 */
export function formatBitrateFromBytes(bytesPerSec: number): string {
  const bitsPerSec = bytesPerSec * 8;
  return formatBitrate(bitsPerSec);
}

/**
 * Generate streaming URIs from clientID and eventID
 * Pattern: https://api.v3.invintus.com/StreamURI/hls/{clientID}/{eventID}/media.m3u8
 * @param clientID - The client ID
 * @param eventID - The event ID (not the composite clientID_eventID format)
 * @returns Object with main and backup URIs, or null if inputs are missing
 */
export function generateStreamingURIs(
  clientID: string | null,
  eventID: string | null
): { main: string | null; backup: string | null } | null {
  if (!clientID || !eventID) return null;
  // If eventID is composite (clientID_eventID), extract just the eventID part
  const eventIDPart = eventID.includes('_') ? eventID.split('_')[1] : eventID;
  return {
    main: `https://api.v3.invintus.com/StreamURI/hls/${clientID}/${eventIDPart}/media.m3u8`,
    backup: null,
  };
}
